/**
 * 云牛集 · 品牌官网 — 全局常量
 *
 * 宣传图片统一走腾讯云 COS（对象存储），本地 public/images 保留作为 COS 不可达时的回退。
 * 切换图片存储时只需修改 COS_BASE 一处。
 *
 * 例外：真实产品截图放在 public/screenshots/ 下，尚未上传 COS，统一用 shotUrl() 引用。
 */
import type { SyntheticEvent } from 'react';

/** COS 公网访问根地址（生产对象存储，bucket: aiotagro-1326635641, region: ap-nanjing, 前缀: shangdong） */
export const COS_BASE = 'https://aiotagro-1326635641.cos.ap-nanjing.myqcloud.com/shangdong';

/** COS 图片目录前缀（与上传对象键 shangdong/images/ 对齐） */
export const IMAGE_BASE = `${COS_BASE}/images`;

/** 静态图片资源版本号：图片内容更新时递增，用于击穿浏览器/CDN 缓存（同名覆盖时必须同步递增） */
export const IMAGE_VERSION = '20260930';

/**
 * 真实产品截图目录前缀。
 *
 * 与 /images 下的素材不同，截图是直接放在 public/screenshots/ 里的真实界面抓图，
 * 尚未上传对象存储，因此**不能**经 cosUrl() 拼接 COS 域名（会 404）。
 */
export const SHOT_BASE = '/screenshots';

/** 截图资源版本号：截图更新时递增，用于击穿浏览器缓存 */
export const SHOT_VERSION = '20260927';

/**
 * 将本地图片路径（如 /images/cap-trace.jpg）转换为 COS 公网链接。
 * 已是绝对 URL 的（http/https/data:/blob:）原样返回，便于后端直接返回 COS 地址时透传。
 */
export function cosUrl(path: string): string {
  if (!path) return path;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  const name = path.replace(/^\/+/, '').replace(/^images\//, '');
  return `${IMAGE_BASE}/${name}?v=${IMAGE_VERSION}`;
}

/**
 * 引用真实产品截图：直接使用 public/ 下的绝对路径 + 版本戳，不经过 COS。
 *
 * 用法：<img src={shotUrl('shot-trade-hall.jpg')} onError={imgFallback} />
 * 传入的 name 可以是 'shot-xxx.jpg' 或 '/screenshots/shot-xxx.jpg'，两种写法等价。
 * 与 cosUrl() 的区别：截图尚未上云，拼接 COS 地址必然 404，故必须走本路径。
 */
export function shotUrl(name: string): string {
  if (!name) return name;
  if (/^(https?:|data:|blob:)/i.test(name)) return name;
  const file = name.replace(/^\/+/, '').replace(/^screenshots\//, '');
  return `${SHOT_BASE}/${file}?v=${SHOT_VERSION}`;
}

/**
 * <img> 加载失败时回退到本地图。
 * 用法：<img src={cosUrl('/images/x.jpg')} data-local="/images/x.jpg" onError={imgFallback} />
 * 本地图也失败时停止重试，避免死循环。
 */
export function imgFallback(e: SyntheticEvent<HTMLImageElement>): void {
  const el = e.currentTarget;
  const local = el.dataset.local;
  if (local && el.src !== local) {
    el.src = local;
    el.onerror = null;
  }
}
